import type { RefObject } from 'react';
import { useEffect } from 'react';

/**
 * Scroll reveal for `[data-reveal]` and the direct children of `[data-reveal-group]` inside `rootRef`
 * (ported from the design's shell). The hidden start state lives in the CSS module; this sets
 * `data-revealed` once an element enters the lower ~88% of the viewport, staggering group children
 * through `--reveal-i`. Without IntersectionObserver, or under reduced motion, everything shows at once.
 * Once visible, always visible.
 */
export function useReveal(rootRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const items: HTMLElement[] = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    root.querySelectorAll<HTMLElement>('[data-reveal-group]').forEach((group) => {
      Array.from(group.children).forEach((child, index) => {
        if (!(child instanceof HTMLElement)) return;
        if (!child.style.getPropertyValue('--reveal-i')) child.style.setProperty('--reveal-i', String(index));
        items.push(child);
      });
    });
    if (!items.length) return undefined;

    const show = (element: HTMLElement) => element.setAttribute('data-revealed', '');
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window) || reduced) {
      items.forEach(show);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target as HTMLElement);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -12% 0px' }
    );
    items.forEach((element) => observer.observe(element));

    // One delayed sweep: anything the observer has not reported (a zero-height slot, a print pass) is shown.
    const sweep = window.setTimeout(() => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      items.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < viewportHeight) show(element);
      });
    }, 900);

    return () => {
      window.clearTimeout(sweep);
      observer.disconnect();
    };
  }, [rootRef]);
}
